import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Save, Trash2, X as XIcon, GraduationCap, ChevronDown, ChevronRight, MessageSquarePlus } from "lucide-react";
import { api } from "../lib/api";
import { useAuth, isAdmin } from "../contexts/AuthContext";
import { SchoolLogo, AcademicSnapshot, StaffTable, LevelPill, KenPomStrip, DivisionTag, SportTag, ratingLabel } from "./CoachDbParts";

const INTEREST_LEVELS = ["Watching", "We Contacted", "Contacted", "Offered", "Committed", "Decommitted"];
const SCHOLARSHIP_TYPES = ["", "Full", "Partial", "Preferred Walk-On", "No"];
const SCHOOL_LEVELS = ["", "D1 High Major", "D1 Mid Major +", "D1 Mid Major", "D1 Low Major", "NCAA D1", "NCAA D2", "D3 High Academic", "NCAA D3", "JUCO", "NAIA", "PREP"];
const levelFor = (p) => {
  if (!p) return "";
  if (p.division === "NCAA D3") return p.level === "High Academic" ? "D3 High Academic" : "NCAA D3";
  if (p.division && p.division !== "NCAA D1") return p.division;
  return p.level ? `D1 ${p.level}` : "NCAA D1";
};

const LEVEL_STYLES = {
  Committed:      "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  Offered:        "bg-amber-500/20 text-amber-300 border-amber-500/40",
  Contacted:      "bg-blue-500/20 text-blue-300 border-blue-500/40",
  "We Contacted": "bg-violet-500/20 text-violet-300 border-violet-500/40",
  Watching:       "bg-white/10 text-[#c0c0c0] border-white/20",
  Decommitted:    "bg-red-500/20 text-red-300 border-red-500/40",
};

const EMPTY = {
  id: null,
  college_name: "",
  program_id: null,
  program: null,
  interest_level: "Watching",
  level: "",
  league: "",
  coach_name: "",
  coach_contact: "",
  last_contact_date: new Date().toISOString().slice(0, 10),
  scholarship_type: "",
  notes: "",
};

export default function RecruitmentTracker({ playerId, playerName }) {
  const { user } = useAuth();
  const canEdit = isAdmin(user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingDraft, setEditingDraft] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [noteFocusId, setNoteFocusId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.listRecruitment(playerId);
      setRows(data || []);
    } catch (e) {
      toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [playerId]);

  const startNew = () => setDraft({ ...EMPTY });
  const cancelNew = () => setDraft(null);

  const saveNew = async () => {
    if (!draft.college_name.trim()) { toast.error("College name required"); return; }
    try {
      const payload = { ...draft, notes: draft.notes || null,
                        coach_name: draft.coach_name || null,
                        coach_contact: draft.coach_contact || null,
                        scholarship_type: draft.scholarship_type || null,
                        level: draft.level || null,
                        league: (draft.league || "").trim() || null,
                        last_contact_date: draft.last_contact_date || null };
      delete payload.id; delete payload.program;
      await api.createRecruitment(playerId, payload);
      toast.success(`${draft.college_name} added`);
      setDraft(null);
      load();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const startEdit = (row) => { setEditingId(row.id); setEditingDraft({ ...EMPTY, ...row }); };
  const cancelEdit = () => { setEditingId(null); setEditingDraft(null); };
  const saveEdit = async () => {
    if (!editingDraft.college_name.trim()) { toast.error("College name required"); return; }
    try {
      const payload = { ...editingDraft, notes: editingDraft.notes || null,
                        coach_name: editingDraft.coach_name || null,
                        coach_contact: editingDraft.coach_contact || null,
                        scholarship_type: editingDraft.scholarship_type || null,
                        level: editingDraft.level || null,
                        league: (editingDraft.league || "").trim() || null,
                        last_contact_date: editingDraft.last_contact_date || null };
      delete payload.program;
      await api.updateRecruitment(playerId, editingId, payload);
      toast.success("Updated");
      cancelEdit();
      load();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.college_name} from ${playerName}'s recruitment list?`)) return;
    try {
      await api.deleteRecruitment(playerId, row.id);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const addFeedback = async (row, date, note) => {
    try {
      await api.addRecruitmentFeedback(playerId, row.id, { date, note });
      toast.success("Note added");
      load();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const removeFeedback = async (row, entryId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.deleteRecruitmentFeedback(playerId, row.id, entryId);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <section className="border border-white/10 bg-[#101010]" data-testid="recruitment-tracker">
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <GraduationCap className="w-4 h-4 text-[var(--org-accent)]" />
          <h3 className="font-heading uppercase text-lg tracking-[0.2em]">Recruitment</h3>
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0]">
            {rows.length} {rows.length === 1 ? "school" : "schools"}
          </span>
        </div>
        {canEdit && !draft && (
          <button
            onClick={startNew}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--org-accent)] text-[#121212] text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-[var(--org-accent-hover)]"
            data-testid="recruit-add-btn"
          >
            <Plus className="w-3 h-3" /> Add College
          </button>
        )}
      </div>

      {draft && (
        <RecruitmentForm draft={draft} setDraft={setDraft} onSave={saveNew} onCancel={cancelNew} testPrefix="new" />
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10 gap-2 text-[#a0a0a0]">
          <Loader2 className="w-4 h-4 animate-spin" /> loading…
        </div>
      ) : rows.length === 0 && !draft ? (
        <div className="text-center py-12 text-[#757575] text-sm">
          No schools yet. {canEdit && "Add the first one above."}
        </div>
      ) : (
        <div className="divide-y divide-white/5">
          {rows.map(r => (
            editingId === r.id ? (
              <RecruitmentForm
                key={r.id} draft={editingDraft} setDraft={setEditingDraft}
                onSave={saveEdit} onCancel={cancelEdit} testPrefix={`edit-${r.id}`}
              />
            ) : (
              <RecruitmentRow
                key={r.id} row={r}
                canEdit={canEdit}
                expanded={expandedId === r.id}
                onToggle={() => { setExpandedId(prev => prev === r.id ? null : r.id); setNoteFocusId(null); }}
                onLogNote={() => { setExpandedId(r.id); setNoteFocusId(r.id); }}
                focusNote={noteFocusId === r.id}
                onEdit={() => startEdit(r)}
                onDelete={() => remove(r)}
                onAddFeedback={(date, note) => addFeedback(r, date, note)}
                onRemoveFeedback={(entryId) => removeFeedback(r, entryId)}
              />
            )
          ))}
        </div>
      )}
    </section>
  );
}

// Admin-only: coaching staff + academic snapshot from the Coach Database, matched by school name.
function CoachDbInline({ school, rowId, programId }) {
  const [state, setState] = useState({ loading: true, program: null });
  useEffect(() => {
    let live = true;
    const req = programId ? api.coachDbProgram(programId).then(p => ({ program: p })) : api.coachDbMatch(school);
    req.then(r => live && setState({ loading: false, program: r.program })).catch(() => live && setState({ loading: false, program: null }));
    return () => { live = false; };
  }, [school, programId]);
  if (state.loading) return <div className="px-6 py-3 border-t border-white/[0.06]"><Loader2 className="w-4 h-4 animate-spin text-[#757575]" /></div>;
  if (!state.program) return (
    <div className="px-6 py-3 border-t border-white/[0.06] text-[11px] text-[#757575] italic" data-testid={`recruit-coachdb-none-${rowId}`}>
      No Coach Database match for "{school}" — D1 programs only for now.
    </div>
  );
  const p = state.program;
  return (
    <div className="px-6 py-4 border-t border-white/[0.06] bg-white/[0.015] space-y-3" data-testid={`recruit-coachdb-${rowId}`}>
      <div className="flex items-center gap-3">
        <SchoolLogo program={p} size={32} />
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--org-accent)] flex items-center gap-2">Coach Database · {p.conference} <DivisionTag division={p.division} /><LevelPill level={p.level} /></div>
          <div className="text-sm text-white font-semibold truncate">{p.school} <span className="text-[#757575] font-normal">· {p.city}, {p.state_code}</span></div>
        </div>
      </div>
      <KenPomStrip kenpom={p.kenpom} compact />
      <AcademicSnapshot academics={p.academics} program={p} />
      <StaffTable staff={p.staff} compact />
    </div>
  );
}

function RecruitmentRow({ row, canEdit, expanded, onToggle, onLogNote, focusNote, onEdit, onDelete, onAddFeedback, onRemoveFeedback }) {
  const entries = row.feedback_entries || [];
  const latest = entries[0]; // pre-sorted newest first by backend
  const levelPill = (
    <span className={`inline-flex px-2.5 py-1 border text-[10px] uppercase tracking-[0.2em] font-bold shrink-0 ${LEVEL_STYLES[row.interest_level] || LEVEL_STYLES.Watching}`}>
      {row.interest_level}
    </span>
  );
  return (
    <div data-testid={`recruit-row-${row.id}`}>
      {/* Mobile layout — school + coach + interest pill */}
      <div className="md:hidden px-4 py-3 hover:bg-white/[0.02]">
        <div className="flex items-start gap-3">
          <button
            onClick={onToggle}
            className="flex-1 min-w-0 flex items-start gap-2 text-left"
            data-testid={`recruit-toggle-${row.id}`}
          >
            {expanded
              ? <ChevronDown className="w-4 h-4 text-[#a0a0a0] shrink-0 mt-1" />
              : <ChevronRight className="w-4 h-4 text-[#a0a0a0] shrink-0 mt-1" />}
            {row.program && <SchoolLogo program={row.program} size={30} className="mt-0.5" />}
            <div className="min-w-0 flex-1">
              <div className="font-heading text-base text-white uppercase tracking-tight truncate flex items-center gap-2">{row.college_name}{row.program?.kenpom_rank && <span className="text-[10px] font-body text-[#a0a0a0] normal-case tracking-normal" data-testid={`recruit-kenpom-mobile-${row.id}`}>{ratingLabel(row.program.kenpom_source)} #{row.program.kenpom_rank}</span>}</div>
              {(row.level || row.league || row.program?.level) && (
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--org-accent)] mt-0.5 truncate flex items-center gap-1.5">
                  <SportTag sport={row.program?.sport_key} /><DivisionTag division={row.program?.division} />{row.program?.level && <LevelPill level={row.program.level} />}
                  <span className="truncate">{row.program?.level ? "" : row.level || ""}{!row.program?.level && row.level && row.league ? " · " : ""}{row.league || ""}</span>
                </div>
              )}
              {row.coach_name && (
                <div className="text-[11px] text-[#a0a0a0] mt-0.5 truncate">
                  <span className="text-[#757575]">Coach </span>{row.coach_name}
                </div>
              )}
            </div>
          </button>
          <div className="flex items-center gap-1 shrink-0">
            {levelPill}
            {canEdit && (
              <div className="flex items-center gap-1 ml-1">
                <button onClick={onLogNote} title="Log a dated note"
                  className="p-1.5 border border-[var(--org-accent)]/40 text-[var(--org-accent)] hover:bg-[var(--org-accent)]/10"
                  data-testid={`recruit-lognote-mobile-${row.id}`}
                ><MessageSquarePlus className="w-3 h-3" /></button>
                <button onClick={onEdit} title="Edit school"
                  className="p-1.5 border border-white/10 text-[#a0a0a0] hover:text-white hover:border-white/30"
                  data-testid={`recruit-edit-mobile-${row.id}`}
                ><Pencil className="w-3 h-3" /></button>
                <button onClick={onDelete} title="Delete school"
                  className="p-1.5 border border-white/10 text-[#a0a0a0] hover:text-red-300 hover:border-red-500/40"
                  data-testid={`recruit-delete-mobile-${row.id}`}
                ><Trash2 className="w-3 h-3" /></button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Desktop layout — full 12-column grid */}
      <div className="hidden md:grid px-6 py-4 grid-cols-12 gap-4 items-center hover:bg-white/[0.02]">
        <button
          onClick={onToggle}
          className="col-span-3 flex items-center gap-2 text-left"
          data-testid={`recruit-toggle-desktop-${row.id}`}
        >
          {expanded
            ? <ChevronDown className="w-4 h-4 text-[#a0a0a0] shrink-0" />
            : <ChevronRight className="w-4 h-4 text-[#a0a0a0] shrink-0" />}
          {row.program && <SchoolLogo program={row.program} size={40} />}
          <div className="min-w-0">
            <div className="font-heading text-xl text-white uppercase tracking-tight truncate flex items-center gap-2">
              {row.college_name}
              {row.program?.kenpom_rank && <span className="text-[10px] font-body text-[#a0a0a0] normal-case tracking-normal border border-white/10 px-1.5 h-5 inline-flex items-center" data-testid={`recruit-kenpom-${row.id}`}>{ratingLabel(row.program.kenpom_source)} #{row.program.kenpom_rank}{row.program.kenpom_record ? ` · ${row.program.kenpom_record}` : ""}</span>}
            </div>
            {(row.level || row.league || row.program?.level) && (
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--org-accent)] mt-0.5 truncate flex items-center gap-1.5">
                <SportTag sport={row.program?.sport_key} /><DivisionTag division={row.program?.division} />{row.program?.level && <LevelPill level={row.program.level} />}
                <span className="truncate">{row.program?.level ? "" : row.level || ""}{!row.program?.level && row.level && row.league ? " · " : ""}{row.league || ""}</span>
              </div>
            )}
            {row.coach_name && (
              <div className="text-xs text-[#a0a0a0] mt-1 truncate">
                <span className="text-[#757575]">Coach </span>{row.coach_name}
              </div>
            )}
          </div>
        </button>
        <div className="col-span-2">
          {levelPill}
        </div>
        <div className="col-span-2">
          <div className="text-[10px] uppercase tracking-widest text-[#757575]">Scholarship</div>
          <div className="text-sm text-white">{row.scholarship_type || "—"}</div>
        </div>
        <div className="col-span-2">
          <div className="text-[10px] uppercase tracking-widest text-[#757575]">Last Contact</div>
          <div className="text-sm text-white">{row.last_contact_date || "—"}</div>
        </div>
        <div className={`${canEdit ? "col-span-1" : "col-span-3"} min-w-0`}>
          {latest ? (
            <div className="text-xs text-[#c0c0c0] truncate" title={latest.note}>
              <span className="text-[#757575] font-mono mr-1">{latest.date}</span>
              {latest.note}
            </div>
          ) : row.notes ? (
            <div className="text-sm text-[#c0c0c0] line-clamp-2">{row.notes}</div>
          ) : (
            <span className="text-[#555] text-sm">No feedback yet</span>
          )}
        </div>
        {canEdit && (
          <div className="col-span-2 flex items-center justify-end gap-1">
            <button
              onClick={onLogNote} title="Log a dated note"
              className="flex items-center gap-1.5 px-2.5 py-2 border border-[var(--org-accent)]/40 text-[var(--org-accent)] text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[var(--org-accent)]/10 whitespace-nowrap"
              data-testid={`recruit-lognote-${row.id}`}
            ><MessageSquarePlus className="w-3 h-3" /> Log Note</button>
            <button
              onClick={onEdit} title="Edit school"
              className="p-2 border border-white/10 text-[#a0a0a0] hover:text-white hover:border-white/30"
              data-testid={`recruit-edit-${row.id}`}
            ><Pencil className="w-3 h-3" /></button>
            <button
              onClick={onDelete} title="Delete school"
              className="p-2 border border-white/10 text-[#a0a0a0] hover:text-red-300 hover:border-red-500/40"
              data-testid={`recruit-delete-${row.id}`}
            ><Trash2 className="w-3 h-3" /></button>
          </div>
        )}
      </div>

      {expanded && canEdit && <CoachDbInline school={row.college_name} rowId={row.id} programId={row.program_id} />}
      {expanded && (
        <FeedbackLog
          rowId={row.id}
          entries={entries}
          canEdit={canEdit}
          onAdd={onAddFeedback}
          onRemove={onRemoveFeedback}
          summaryNote={row.notes}
          autoFocus={focusNote}
        />
      )}
    </div>
  );
}

function FeedbackLog({ rowId, entries, canEdit, onAdd, onRemove, summaryNote, autoFocus }) {
  const noteRef = useRef(null);
  useEffect(() => { if (autoFocus && noteRef.current) { noteRef.current.focus(); noteRef.current.scrollIntoView({ block: "center", behavior: "smooth" }); } }, [autoFocus]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!note.trim()) { toast.error("Note text required"); return; }
    setSaving(true);
    try {
      await onAdd(date, note.trim());
      setNote("");
      setDate(new Date().toISOString().slice(0, 10));
    } finally { setSaving(false); }
  };

  return (
    <div className="px-8 pb-6 pt-2 bg-[#141414] border-t border-white/5" data-testid={`recruit-feedback-log-${rowId}`}>
      {summaryNote && (
        <div className="mb-4 p-3 border-l-2 border-[var(--org-accent)] bg-white/[0.02]">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--org-accent)] mb-1">Headline</div>
          <div className="text-sm text-white">{summaryNote}</div>
        </div>
      )}

      {canEdit && (
        <div className="mb-4 grid grid-cols-12 gap-2" data-testid={`recruit-feedback-form-${rowId}`}>
          <div className="col-span-2">
            <input
              type="date" value={date} onChange={e => setDate(e.target.value)}
              className="w-full bg-[#101010] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
              data-testid={`recruit-feedback-date-${rowId}`}
            />
          </div>
          <div className="col-span-8">
            <input
              ref={noteRef}
              value={note}
              onChange={e => setNote(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
              placeholder="What did we hear? Assistant called, offered a visit, timeline update…"
              className="w-full bg-[#101010] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
              data-testid={`recruit-feedback-note-${rowId}`}
            />
          </div>
          <div className="col-span-2">
            <button
              onClick={submit} disabled={saving || !note.trim()}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-[var(--org-accent)] text-[#121212] text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-[var(--org-accent-hover)] disabled:opacity-60"
              data-testid={`recruit-feedback-submit-${rowId}`}
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageSquarePlus className="w-3 h-3" />}
              Log Note
            </button>
          </div>
        </div>
      )}

      {entries.length === 0 ? (
        <div className="text-center py-6 text-[#555] text-xs uppercase tracking-widest">
          No feedback logged yet
        </div>
      ) : (
        <ul className="space-y-2">
          {entries.map(e => (
            <li key={e.id} className="flex items-start gap-3 p-3 border-l-2 border-white/10 bg-white/[0.02] hover:border-[var(--org-accent)]"
                data-testid={`recruit-feedback-entry-${e.id}`}
                style={{ transition: "border-color 0.2s ease" }}>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--org-accent)] shrink-0 mt-0.5 w-24">
                {e.date}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white whitespace-pre-wrap">{e.note}</div>
                {e.author && (
                  <div className="mt-1 text-[10px] uppercase tracking-widest text-[#757575]">— {e.author}</div>
                )}
              </div>
              {canEdit && (
                <button
                  onClick={() => onRemove(e.id)} title="Delete note"
                  className="p-1.5 text-[#555] hover:text-red-300"
                  data-testid={`recruit-feedback-remove-${e.id}`}
                ><Trash2 className="w-3 h-3" /></button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RecruitmentForm({ draft, setDraft, onSave, onCancel, testPrefix }) {
  const upd = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [sport, setSport] = useState("mens");
  const linked = draft.program || null;

  useEffect(() => {
    if (linked || !draft.college_name || draft.college_name.trim().length < 2) { setResults([]); return; }
    let live = true;
    const t = setTimeout(() => api.coachDbSearch(draft.college_name.trim(), sport).then(r => { if (live) { setResults(r.results || []); setOpen(true); } }).catch(() => {}), 220);
    return () => { live = false; clearTimeout(t); };
  }, [draft.college_name, linked, sport]);

  const pick = (p) => {
    setDraft(d => ({ ...d,
      college_name: p.short_name || p.school, program_id: p.id, program: p,
      level: levelFor(p),
      league: p.conference || d.league || "",
      coach_name: p.head_coach || d.coach_name || "",
      coach_contact: p.head_coach_email || d.coach_contact || "",
    }));
    setOpen(false); setResults([]);
  };
  const unlink = () => setDraft(d => ({ ...d, program_id: null, program: null }));

  return (
    <div className="px-6 py-5 bg-[#141414] border-b border-white/10" data-testid={`recruit-form-${testPrefix}`}>
      <div className="grid grid-cols-12 gap-3">
        <Field label="College" span={4} required>
          <div className="relative">
            {linked ? (
              <div className="w-full bg-[#101010] border border-[var(--org-accent)]/50 px-2 py-1.5 flex items-center gap-2" data-testid={`recruit-linked-${testPrefix}`}>
                <SchoolLogo program={linked} size={26} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">{draft.college_name}</div>
                  <div className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0] truncate">{[linked.level, linked.conference].filter(Boolean).join(" · ")} · linked</div>
                </div>
                <button type="button" onClick={unlink} className="text-[#757575] hover:text-white p-1" title="Unlink from Coach Database" data-testid={`recruit-unlink-${testPrefix}`}><XIcon className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <input
                value={draft.college_name} onChange={e => upd("college_name", e.target.value)} onFocus={() => results.length && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
                placeholder="Start typing a school…" autoComplete="off"
                className="w-full bg-[#101010] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
                data-testid={`recruit-input-college-${testPrefix}`}
              />
            )}
            {open && !linked && results.length > 0 && (
              <ul className="absolute z-30 left-0 right-0 mt-1 bg-[#0e0e0e] border border-white/15 shadow-2xl max-h-72 overflow-auto" data-testid={`recruit-typeahead-${testPrefix}`}>
                {results.map(p => (
                  <li key={p.id}>
                    <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => pick(p)} className="w-full flex items-center gap-2.5 px-2.5 py-2 text-left hover:bg-white/[0.05]" data-testid={`recruit-typeahead-opt-${p.id}`}>
                      <SchoolLogo program={p} size={28} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-white truncate">{p.short_name || p.school}</div>
                        <div className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0] truncate flex items-center gap-1.5"><DivisionTag division={p.division} /><LevelPill level={p.level} /><span className="truncate">{p.conference}</span></div>
                      </div>
                      {p.kenpom_rank && <span className="text-[10px] text-[#757575] shrink-0">KP #{p.kenpom_rank}</span>}
                    </button>
                  </li>
                ))}
                <li className="px-2.5 py-1.5 text-[9px] uppercase tracking-widest text-[#555] border-t border-white/10 flex items-center justify-between">
                  <span>Not listed? Keep typing to add as free text</span>
                  <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => setSport(s => s === "mens" ? "womens" : "mens")} className="text-[var(--org-accent)] hover:text-white" data-testid={`recruit-typeahead-sport-${testPrefix}`}>{sport === "mens" ? "Searching men's · switch to women's" : "Searching women's · switch to men's"}</button>
                </li>
              </ul>
            )}
          </div>
        </Field>
        <Field label="Level" span={2}>
          <select
            value={draft.level || ""} onChange={e => upd("level", e.target.value)}
            className="w-full bg-[#101010] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
            data-testid={`recruit-input-schoollevel-${testPrefix}`}
          >
            {SCHOOL_LEVELS.map(l => <option key={l} value={l}>{l || "—"}</option>)}
          </select>
        </Field>
        <Field label="League / Conference" span={3}>
          <input
            value={draft.league || ""} onChange={e => upd("league", e.target.value)}
            placeholder="Big Ten, Ivy, NEC..."
            className="w-full bg-[#101010] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
            data-testid={`recruit-input-league-${testPrefix}`}
          />
        </Field>
        <Field label="Interest Level" span={3}>
          <select
            value={draft.interest_level} onChange={e => upd("interest_level", e.target.value)}
            className="w-full bg-[#101010] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
            data-testid={`recruit-input-level-${testPrefix}`}
          >
            {INTEREST_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </Field>
        <Field label="Scholarship" span={3}>
          <select
            value={draft.scholarship_type || ""} onChange={e => upd("scholarship_type", e.target.value)}
            className="w-full bg-[#101010] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
            data-testid={`recruit-input-scholarship-${testPrefix}`}
          >
            {SCHOLARSHIP_TYPES.map(t => <option key={t} value={t}>{t || "—"}</option>)}
          </select>
        </Field>
        <Field label="Last Contact" span={3}>
          <input
            type="date" value={draft.last_contact_date || ""} onChange={e => upd("last_contact_date", e.target.value)}
            className="w-full bg-[#101010] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
            data-testid={`recruit-input-date-${testPrefix}`}
          />
        </Field>

        <Field label="Coach" span={4}>
          <input
            value={draft.coach_name || ""} onChange={e => upd("coach_name", e.target.value)}
            placeholder="Head coach name"
            className="w-full bg-[#101010] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
            data-testid={`recruit-input-coach-${testPrefix}`}
          />
        </Field>
        <Field label="Coach Contact" span={4}>
          <input
            value={draft.coach_contact || ""} onChange={e => upd("coach_contact", e.target.value)}
            placeholder="phone / email"
            className="w-full bg-[#101010] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
            data-testid={`recruit-input-contact-${testPrefix}`}
          />
        </Field>
        <Field label="Headline / Summary" span={12}>
          <input
            value={draft.notes || ""} onChange={e => upd("notes", e.target.value)}
            placeholder="One-line status (e.g. Full ride, HC really likes him). Detailed feedback lives in the log."
            className="w-full bg-[#101010] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
            data-testid={`recruit-input-notes-${testPrefix}`}
          />
          <div className="text-[10px] text-[#757575] mt-1.5">This is the one-line headline. For dated updates (calls, visits, offers) save, then use <span className="text-[var(--org-accent)] font-bold">Log Note</span> on the school's row — every note keeps its own date.</div>
        </Field>
      </div>
      <div className="flex items-center justify-end gap-2 mt-4">
        <button
          onClick={onCancel}
          className="flex items-center gap-2 px-4 py-2 border border-white/20 text-[#a0a0a0] text-[10px] uppercase tracking-[0.3em] font-bold hover:text-white"
          data-testid={`recruit-form-cancel-${testPrefix}`}
        ><XIcon className="w-3 h-3" /> Cancel</button>
        <button
          onClick={onSave}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--org-accent)] text-[#121212] text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-[var(--org-accent-hover)]"
          data-testid={`recruit-form-save-${testPrefix}`}
        ><Save className="w-3 h-3" /> Save</button>
      </div>
    </div>
  );
}

function Field({ label, span, required, children }) {
  const cls = `col-span-${span}`;
  return (
    <div className={cls}>
      <div className="text-[10px] uppercase tracking-widest text-[#757575] mb-1">
        {label}{required && <span className="text-red-400 ml-1">*</span>}
      </div>
      {children}
    </div>
  );
}
